"use client";

import { motion } from "framer-motion";
import { Language } from "@linqx/i18n";
import { cn } from "@/lib/utils";
import { Reveal, Parallax } from "@/components/motion/scroll-fx";
import {
  Icon20MoonOutline,
  Icon20HomeOutline,
  Icon20CalendarOutline,
  Icon20SparkleOutline,
  Icon20CupOutline,
  Icon20BrushOutline,
  Icon20MusicOutline,
  Icon20FootballBallOutline,
  Icon20PlaneOutline,
  Icon20Stars,
  Icon20GlobeOutline,
  Icon20ChristmasTreeOutline,
  Icon20Users3Outline,
  Icon20PlaceOutline,
} from "@linqx/icons/web";
import type { ComponentType } from "react";

const vibes = [
  { labelKey: "landing_features_vibe_adventure", icon: Icon20PlaceOutline },
  { labelKey: "landing_features_vibe_lowkey", icon: Icon20MoonOutline },
  {
    labelKey: "landing_features_vibe_outdoor",
    icon: Icon20ChristmasTreeOutline,
  },
  { labelKey: "landing_features_vibe_indoor", icon: Icon20HomeOutline },
  { labelKey: "landing_features_vibe_events", icon: Icon20CalendarOutline },
  { labelKey: "landing_features_vibe_nightlife", icon: Icon20SparkleOutline },
  { labelKey: "landing_features_vibe_foodie", icon: Icon20CupOutline },
  { labelKey: "landing_features_vibe_coffee", icon: Icon20CupOutline },
  { labelKey: "landing_features_vibe_art", icon: Icon20BrushOutline },
  { labelKey: "landing_features_vibe_music", icon: Icon20MusicOutline },
  { labelKey: "landing_features_vibe_sports", icon: Icon20FootballBallOutline },
  { labelKey: "landing_features_vibe_travel", icon: Icon20PlaneOutline },
];

const featureCards = [
  {
    titleKey: "landing_features_card_match_title",
    descriptionKey: "landing_features_card_match_desc",
    icon: Icon20Stars,
  },
  {
    titleKey: "landing_features_card_experience_title",
    descriptionKey: "landing_features_card_experience_desc",
    icon: Icon20GlobeOutline,
  },
  {
    titleKey: "landing_features_card_build_title",
    descriptionKey: "landing_features_card_build_desc",
    icon: Icon20Users3Outline,
  },
];

export function Features() {
  return (
    <section
      id="features"
      className="py-32 md:py-48 relative overflow-hidden flex items-center min-h-screen"
    >
      {/* Flat section background */}
      <div className="absolute inset-0 bg-background" />

      {/* Single subtle brand bloom for atmosphere */}
      <div className="pointer-events-none absolute -left-24 top-10 -z-0">
        <div className="h-80 w-80 rounded-full bg-brand-200/30 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10 w-full">
        {/* Section header */}
        <Reveal direction="up" distance={56}>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-heading font-bold mb-4">
              {Language.getKey("landing_features_title_prefix")}
              <span className="text-foreground">
                {Language.getKey("landing_features_title_highlight")}
              </span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {Language.getKey("landing_features_subtitle")}
            </p>
          </div>
        </Reveal>

        {/* Floating vibe tags — the whole cloud scales in and drifts out */}
        <Reveal direction="scale" distance={48}>
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {vibes.map((vibe, index) => (
              <VibeTag key={vibe.labelKey} {...vibe} index={index} />
            ))}
          </div>
        </Reveal>

        {/* Feature cards — alternating sides with parallax depth offset */}
        <div className="grid md:grid-cols-3 gap-6 mt-24">
          {featureCards.map((card, index) => (
            <Reveal
              key={card.titleKey}
              direction={index % 2 === 0 ? "left" : "right"}
              distance={96}
              delay={index * 0.04}
            >
              <Parallax distance={index % 2 === 0 ? 36 : -28}>
                <FeatureCard {...card} index={index} />
              </Parallax>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

interface VibeTagProps {
  labelKey: string;
  icon: ComponentType<{ className?: string }>;
  index: number;
}

function VibeTag({ labelKey, icon: Icon, index }: VibeTagProps) {
  // Generate unique float animation for each tag
  const floatY = [0, -6, 0, 6, 0];
  const floatDuration = 4 + (index % 3);
  const floatDelay = index * 0.15;

  // Alternate between dark and light brand colors
  const isEven = index % 2 === 0;

  return (
    <motion.div
      animate={{ y: floatY }}
      transition={{
        duration: floatDuration,
        repeat: Infinity,
        delay: floatDelay,
        ease: "easeInOut",
      }}
      whileHover={{ scale: 1.12, y: -6, rotate: isEven ? -2 : 2 }}
      whileTap={{ scale: 0.95 }}
      className={cn(
        "px-5 py-2.5 rounded-full cursor-pointer flex items-center gap-2 shadow-lg hover:shadow-xl transition-shadow duration-300 text-white",
        isEven ? "bg-brand-900" : "bg-brand-500",
      )}
    >
      <Icon className="text-white/90" />
      <span className="font-semibold">{Language.getKey(labelKey)}</span>
    </motion.div>
  );
}

interface FeatureCardProps {
  titleKey: string;
  descriptionKey: string;
  icon: ComponentType<{ className?: string }>;
  index: number;
}

function FeatureCard({
  titleKey,
  descriptionKey,
  icon: Icon,
}: FeatureCardProps) {
  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      className="h-full"
    >
      <div className="h-full p-6 rounded-2xl border border-border bg-card hover:border-brand-300 hover:shadow-lg hover:shadow-brand-100 transition-all duration-300 group">
        <motion.div
          whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
          transition={{ duration: 0.3 }}
          className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center mb-4"
        >
          <Icon className="text-brand-600 w-6 h-6" />
        </motion.div>
        <h3 className="text-xl font-semibold mb-2 group-hover:text-brand-800 transition-colors">
          {Language.getKey(titleKey)}
        </h3>
        <p className="text-muted-foreground">
          {Language.getKey(descriptionKey)}
        </p>
      </div>
    </motion.div>
  );
}
